import { useEffect, useMemo, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";

const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const d = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");
const MAX_SUB = 5;
const MAX_CHILD = 5;

export default function MsKeyViewAll() {
  const { http } = AuthUser();
  const [tree, setTree] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openMasters, setOpenMasters] = useState({});
  const [openSubs, setOpenSubs] = useState({});

  useEffect(() => {
    setIsLoading(true);
    http
      .get("/ms_keys_tree")
      .then((res) => {
        setIsLoading(false);
        const data = res.data.data || [];
        setTree(data);
        const masters = {};
        const subs = {};
        data.forEach((m) => {
          masters[m.id] = true;
          (m.sub_keys || []).forEach((s) => {
            subs[s.id] = true;
          });
        });
        setOpenMasters(masters);
        setOpenSubs(subs);
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Error loading view");
      });
  }, []);

  const matches = (text, q) => String(text || "").toLowerCase().includes(q);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map((master) => {
        const masterHit =
          matches(master.master_key_id, q) || matches(master.remarks, q);
        const subKeys = (master.sub_keys || [])
          .map((sub) => {
            const subHit =
              matches(sub.share_name, q) ||
              matches(sub.user_name, q) ||
              matches(sub.user_email, q) ||
              matches(sub.user_mobile, q);
            const children = (sub.child_keys || []).filter(
              (c) =>
                matches(c.company_name, q) ||
                matches(c.location, q) ||
                matches(c.contact_person, q) ||
                matches(c.contact_number, q) ||
                matches(c.license_details, q) ||
                matches(c.status, q)
            );
            if (masterHit || subHit || children.length) {
              return { ...sub, child_keys: masterHit || subHit ? sub.child_keys || [] : children };
            }
            return null;
          })
          .filter(Boolean);
        if (masterHit || subKeys.length) {
          return { ...master, sub_keys: masterHit ? master.sub_keys || [] : subKeys };
        }
        return null;
      })
      .filter(Boolean);
  }, [tree, search]);

  const totals = useMemo(() => {
    let masters = 0;
    let subs = 0;
    let children = 0;
    let amount = 0;
    filtered.forEach((m) => {
      masters += 1;
      (m.sub_keys || []).forEach((s) => {
        subs += 1;
        (s.child_keys || []).forEach((c) => {
          children += 1;
          amount += Number(c.rate_details || 0);
        });
      });
    });
    return { masters, subs, children, amount };
  }, [filtered]);

  const toggleMaster = (id) =>
    setOpenMasters((p) => ({ ...p, [id]: !p[id] }));
  const toggleSub = (id) => setOpenSubs((p) => ({ ...p, [id]: !p[id] }));

  const expandAll = (open) => {
    const masters = {};
    const subs = {};
    tree.forEach((m) => {
      masters[m.id] = open;
      (m.sub_keys || []).forEach((s) => {
        subs[s.id] = open;
      });
    });
    setOpenMasters(masters);
    setOpenSubs(subs);
  };

  return (
    <div className="viewall-page">
      <Toaster />
      <div className="viewall-top">
        <div>
          <h4 className="mb-1">MS Key — View All</h4>
          <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
            Tree view · Master → Sub Key → Child Key (max {MAX_SUB} sub / {MAX_CHILD} child)
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="stat-pill">Masters <b>{totals.masters}</b></div>
          <div className="stat-pill">Sub Keys <b>{totals.subs}</b></div>
          <div className="stat-pill">Child Keys <b>{totals.children}</b></div>
          <div className="stat-pill amount">Total Rate <b>{money(totals.amount)}</b></div>
        </div>
      </div>

      <div className="viewall-toolbar">
        <input
          className="form-control"
          style={{ maxWidth: "360px" }}
          placeholder="Search company, contact, share, master..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn btn-sm btn-outline-secondary" onClick={() => expandAll(true)}>
          Expand all
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => expandAll(false)}>
          Collapse all
        </button>
      </div>

      {isLoading ? (
        <div className="p-3"><TaskSkeleton /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5">No records found</div>
      ) : (
        <div className="viewall-body">
          {filtered.map((master) => {
            const subUsed = (master.sub_keys || []).length;
            const subLeft = Math.max(0, MAX_SUB - subUsed);
            const childTotal = (master.sub_keys || []).reduce(
              (n, s) => n + (s.child_keys || []).length,
              0
            );
            return (
            <div key={master.id} className="master-block">
              <button type="button" className="master-head" onClick={() => toggleMaster(master.id)}>
                <span className="chev">{openMasters[master.id] ? "▼" : "▶"}</span>
                <span className="badge-l">MASTER</span>
                <span className="title">{master.master_key_id}</span>
                <span className="meta">
                  {master.duration_years ? `${master.duration_years} Yr` : "—"} · {d(master.activated_date)} → {d(master.activated_end_date)}
                </span>
                <span className="quota-wrap">
                  <span className="q-pill">Sub used <b>{subUsed}/{MAX_SUB}</b></span>
                  <span className={`q-pill ${subLeft === 0 ? "full" : "ok"}`}>Left <b>{subLeft}</b></span>
                  <span className="q-pill">Children <b>{childTotal}</b></span>
                </span>
              </button>

              {openMasters[master.id] && (
                <div className="master-body">
                  {(master.sub_keys || []).length === 0 ? (
                    <div className="empty-line">No sub keys under this master</div>
                  ) : (
                    (master.sub_keys || []).map((sub) => {
                      const childUsed = (sub.child_keys || []).length;
                      const childLeft = Math.max(0, MAX_CHILD - childUsed);
                      return (
                      <div key={sub.id} className="sub-block">
                        <button type="button" className="sub-head" onClick={() => toggleSub(sub.id)}>
                          <span className="chev">{openSubs[sub.id] ? "▼" : "▶"}</span>
                          <span className="badge-l sub">SUB</span>
                          <span className="title">{sub.share_name || `Sub #${sub.id}`}</span>
                          <span className="meta">
                            {sub.company_name ? `${sub.company_name} · ` : ""}
                            {sub.user_name || "—"} · {sub.user_email || "—"} · {sub.user_mobile || "—"}
                          </span>
                          <span className="quota-wrap">
                            <span className="q-pill">Child used <b>{childUsed}/{MAX_CHILD}</b></span>
                            <span className={`q-pill ${childLeft === 0 ? "full" : "ok"}`}>Left <b>{childLeft}</b></span>
                          </span>
                        </button>
                        {openSubs[sub.id] && (
                          <div className="child-wrap">
                            {(sub.child_keys || []).length === 0 ? (
                              <div className="empty-line">No child keys</div>
                            ) : (
                              <table className="table table-sm mb-0 child-grid">
                                <thead>
                                  <tr>
                                    <th>Company</th>
                                    <th>Location</th>
                                    <th>Contact</th>
                                    <th>Phone</th>
                                    <th>Installed</th>
                                    <th>Duration</th>
                                    <th>Ending</th>
                                    <th>Renew</th>
                                    <th>Qty</th>
                                    <th>Systems</th>
                                    <th>Rate</th>
                                    <th>Rate Details</th>
                                    <th>Status</th>
                                    <th>License</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(sub.child_keys || []).map((c) => (
                                    <tr key={c.id}>
                                      <td className="fw-semibold">{c.company_name || "—"}</td>
                                      <td>{c.location || "—"}</td>
                                      <td>{c.contact_person || "—"}</td>
                                      <td>{c.contact_number || "—"}</td>
                                      <td>{d(c.installed_date)}</td>
                                      <td>
                                        {c.duration_years
                                          ? `${c.duration_years} Yr`
                                          : c.duration_days
                                          ? `${c.duration_days} days`
                                          : "—"}
                                      </td>
                                      <td>{d(c.ending_on)}</td>
                                      <td>{d(c.to_renew)}</td>
                                      <td>{c.qty ?? "—"}</td>
                                      <td>{c.total_systems ?? "—"}</td>
                                      <td>{money(c.rate)}</td>
                                      <td className="fw-semibold">{money(c.rate_details)}</td>
                                      <td>
                                        {c.status ? (
                                          <span className={`badge ${c.status === "Active" ? "bg-success" : c.status === "Expired" ? "bg-danger" : "bg-secondary"}`}>
                                            {c.status}
                                          </span>
                                        ) : "—"}
                                      </td>
                                      <td>{c.license_details || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      <style>{`
        .viewall-page {
          min-height: calc(100vh - 70px);
          background: #f1f5f9;
          padding: 16px 20px 32px;
        }
        .viewall-top {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .stat-pill {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 12px;
          color: #64748b;
        }
        .stat-pill b { color: #0f172a; margin-left: 4px; }
        .stat-pill.amount { background: #ecfdf5; border-color: #a7f3d0; }
        .viewall-toolbar {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }
        .viewall-body { display: flex; flex-direction: column; gap: 12px; }
        .master-block {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
        }
        .master-head, .sub-head {
          width: 100%;
          border: 0;
          background: #0f172a;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          text-align: left;
        }
        .sub-head { background: #134e4a; }
        .chev { width: 18px; }
        .badge-l {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .08em;
          background: #14b8a6;
          color: #042f2e;
          padding: 2px 7px;
          border-radius: 4px;
        }
        .badge-l.sub { background: #99f6e4; }
        .title { font-weight: 700; }
        .meta { color: #cbd5e1; font-size: 12px; }
        .count { margin-left: auto; font-size: 12px; color: #e2e8f0; }
        .quota-wrap {
          margin-left: auto;
          display: inline-flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .q-pill {
          background: rgba(255,255,255,.12);
          border: 1px solid rgba(255,255,255,.2);
          border-radius: 999px;
          padding: 2px 9px;
          font-size: 11px;
          color: #e2e8f0;
        }
        .q-pill b { color: #fff; margin-left: 3px; }
        .q-pill.ok { background: rgba(34,197,94,.25); border-color: rgba(74,222,128,.45); }
        .q-pill.full { background: rgba(239,68,68,.3); border-color: rgba(252,165,165,.5); }
        .master-body { padding: 10px; background: #f8fafc; }
        .sub-block {
          border: 1px solid #d1fae5;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 10px;
          background: #fff;
        }
        .child-wrap { overflow: auto; }
        .child-grid thead th {
          background: #ecfeff;
          font-size: 11px;
          white-space: nowrap;
        }
        .child-grid td { font-size: 12px; white-space: nowrap; }
        .empty-line { padding: 12px; color: #94a3b8; font-size: 13px; }
      `}</style>
    </div>
  );
}
